/**
 * Login con biometría (Face ID / Touch ID / huella) — SÓLO app nativa.
 *
 * Guarda el refresh token en el almacenamiento seguro del sistema (Keychain en
 * iOS, Keystore en Android) mediante `@capgo/capacitor-native-biometric`, y lo
 * recupera únicamente tras una verificación biométrica exitosa. En web todo es
 * no-op / false (gated por `IS_NATIVE`), así el bundle web nunca toca el plugin.
 *
 * Trade-off de seguridad (documentado): el refresh token es un secreto de sesión
 * rotable one-time-use. Vive en Keychain/Keystore (no en localStorage ni en
 * Preferences en claro) y su lectura exige biometría. El backend igual valida el
 * token en cada refresh y lo rota; una copia robada del Keychain no basta sin el
 * factor biométrico del dispositivo. `verifyIdentity()` NO es una frontera de
 * seguridad por sí sola (bypasseable en equipos rooteados) — es una comodidad;
 * la autorización real la sigue dando el backend al validar/rotar el token.
 */
import { IS_NATIVE } from '../base';

/**
 * Clave/servidor bajo el que se indexa la credencial en el Keychain/Keystore.
 * Es un identificador lógico del ítem, no una URL de red.
 */
const BIOMETRIC_SERVER = 'com.tembiapoapp.app.refresh';

/** Usuario placeholder: el plugin exige username; sólo nos importa el password (token). */
const BIOMETRIC_USERNAME = 'tembiapp-session';

/**
 * Carga perezosa del plugin. Lo importamos dinámicamente y sólo en nativo para
 * que el build web no arrastre el binding y para tolerar entornos sin el plugin.
 */
async function getPlugin() {
  if (!IS_NATIVE) return null;
  try {
    const mod = await import('@capgo/capacitor-native-biometric');
    return mod.NativeBiometric;
  } catch {
    return null;
  }
}

/**
 * ¿El dispositivo tiene biometría disponible y configurada? En web siempre false.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const res = await plugin.isAvailable();
    return res.isAvailable === true;
  } catch {
    return false;
  }
}

/**
 * ¿Hay un refresh token guardado para desbloquear con biometría? En web false.
 * No dispara el prompt biométrico (sólo consulta la existencia del ítem); en
 * plataformas donde `getCredentials` exige biometría igual, tratamos cualquier
 * error/ausencia como "no hay token".
 */
export async function hasBiometricToken(): Promise<boolean> {
  const plugin = await getPlugin();
  if (!plugin) return false;
  try {
    const creds = await plugin.getCredentials({ server: BIOMETRIC_SERVER });
    return typeof creds?.password === 'string' && creds.password !== '';
  } catch {
    return false;
  }
}

/**
 * Guarda (o reemplaza) el refresh token en el Keychain/Keystore. No-op en web.
 * Se llama tras un login exitoso en nativo; a partir de ahí el próximo ingreso
 * puede ofrecer el desbloqueo biométrico.
 */
export async function saveRefreshToken(token: string): Promise<void> {
  if (!IS_NATIVE || token === '') return;
  const plugin = await getPlugin();
  if (!plugin) return;
  try {
    // setCredentials sobrescribe el ítem anterior para el mismo server.
    await plugin.setCredentials({
      username: BIOMETRIC_USERNAME,
      password: token,
      server: BIOMETRIC_SERVER,
    });
  } catch {
    // Fail-soft: si no se pudo guardar, el usuario simplemente no tendrá el
    // atajo biométrico y entrará con email + contraseña.
  }
}

/**
 * Pide biometría y, si el usuario se autentica, devuelve el refresh token
 * guardado; `null` si no hay token, la biometría falla o el usuario cancela.
 * En web devuelve null.
 */
export async function getRefreshTokenWithBiometrics(
  reason: string,
  title: string,
): Promise<string | null> {
  const plugin = await getPlugin();
  if (!plugin) return null;
  try {
    // 1) Prompt biométrico. Si el usuario cancela o falla, lanza → catch → null.
    await plugin.verifyIdentity({
      reason,
      title,
      // subtitle/description quedan a criterio del SO; el title alcanza.
    });
    // 2) Sólo tras verificar, leemos la credencial del Keychain/Keystore.
    const creds = await plugin.getCredentials({ server: BIOMETRIC_SERVER });
    const token = creds?.password;
    return typeof token === 'string' && token !== '' ? token : null;
  } catch {
    return null;
  }
}

/**
 * Borra el refresh token guardado (logout o sesión revocada). No-op en web.
 */
export async function clearBiometricToken(): Promise<void> {
  if (!IS_NATIVE) return;
  const plugin = await getPlugin();
  if (!plugin) return;
  try {
    await plugin.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch {
    // Fail-soft: si no había ítem o falló el borrado, no rompemos el logout.
  }
}
